import { ringSizes } from './ring-sizes.js';

class MaterialsColorsManager {
  static colors = {};
  static selectOrderMap = {};
  static sessionKey = 'materials-colors';
  static initPromise = null;

  static async init() {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      await this.getMaterialsAndColors();
      return this;
    })();

    return this.initPromise;
  }

  static async getMaterialsAndColors() {
    const sessionData = this.getSessionData(this.sessionKey);
    if (sessionData) {
      const valueData = sessionData.value;
      if (valueData.status === 'success' && valueData.colors && valueData.materials) {
        this.colors = this.mapColors(valueData.colors);
        this.selectOrderMap = this.mapMaterials(valueData.materials);
      } else {
        await this.fetchAndSetMaterialsAndColors();
      }
    } else {
      await this.fetchAndSetMaterialsAndColors();
    }
  }

  static setSessionData(key, value) {
    const now = new Date().getTime();
    const data = {
      value: value,
      timestamp: now
    };
    sessionStorage.setItem(key, JSON.stringify(data));
  }

  static getSessionData(key) {
    const dataString = sessionStorage.getItem(key);
    if (!dataString) {
      return null;
    }

    const data = JSON.parse(dataString);
    const now = new Date().getTime();
    const one_hour = 3600000;

    const timeElapsed = now - data.timestamp;

    if (timeElapsed > one_hour) {
      sessionStorage.removeItem(key);
      return null;
    }

    return {
      value: data.value,
      timeElapsed: timeElapsed
    };
  }

  static async fetchAndSetMaterialsAndColors() {
    const url = "https://juju-brain-server.cr.juju.business/hollyPalm/materials-and-colors";
    const response = await fetch(url);
    const result = await response.json();

    if (result.status === 'success' && result.colors && result.materials) {
      this.setSessionData(this.sessionKey, result);
      this.colors = this.mapColors(result.colors);
      this.selectOrderMap = this.mapMaterials(result.materials);
    }
  }

  static mapColors(colors) {
    return colors.reduce((map, item) => {
      map[item.sku] = {
        tr: item.title,
        en: item.titleEn,
        sku: item.sku,
        code: item.colorCode,
        order: item.order
      };
      return map;
    }, {});
  }

  static mapMaterials(materials) {
    return materials.reduce((map, item) => {
      map[item.metafield] = {
        sku: item.sku,
        tr: item.title,
        en: item.titleEn,
        order: item.order
      };
      return map;
    }, {});
  }
}


/**
 * Shopify Bundle app ile oluşturulan ürünler için sku oluşturma
 * 
 * - Ürün sayfası yüklendiğinde, `materials-and-colors` API'sinden malzeme ve renk verilerini alır.
 * - Alınan veriler, ürünün SKU'sunu oluşturmak için kullanılır.
 * - Kullanıcı, ürünün boyutunu ve diğer özelliklerini seçtiğinde, SKU dinamik olarak güncellenir.
 * - 'product-variant-picker-bundle.liquid' ile çalışır.
 * - SKU, bundledeki her ürün için `fullSku` input alanına (main-productta bundle ürünler for dönülerek her biri için input alanı bulunur) yazılır ve sepete eklenirken kullanılır.
 * - Kullanıcı, sepete ekle butonuna bastığında, seçili olmayan özellikler için hata mesajları gösterilir.
 */
class BundleProductManager extends HTMLElement {
  constructor() {
    super();
    this.shopLocale = window.theme.shopLocale;
    this.bundleProduct = null;
    this.init();
  }

  async init() {
    try {
      await MaterialsColorsManager.init();
      this.setupMetafieldSelectListeners();
      this.setupSizeRadioListeners();
      this.parseColorsToSelectElements();
      if (this.querySelector('.ringsize-container')) {
        new RingSizeManager(this);
      }
    } catch (error) {
      console.error('BundleProductManager init error:', error);
    }
  }

  setupSizeRadioListeners() {
    const bundleContainer = this.closest('[data-bundle-container]');
    const sizeInputs = bundleContainer?.querySelectorAll('input[type="radio"][name*="Size"]');
    
    if (sizeInputs?.length) {
      sizeInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.updateSku();
        });
      });
    }
  }

  updateSku() {
    const bundleContainer = this.closest('[data-bundle-container]');
    const selectedSizeInput = bundleContainer?.querySelector('input[type="radio"][name*="Size"]:checked');
    const selectedSize = selectedSizeInput?.value;

    const sortedMaterials = this.bundleProduct.selectedValues.sort((a, b) => a.order - b.order);
    let middleSku = '';
    sortedMaterials.forEach((each, i) => {
      let isFirst = i === 0 ? '' : '-';
      middleSku = middleSku + isFirst + `${each.sku}:${each.value}`;
    });

    const baseSku = this.bundleProduct.initialSku;
    const baseSkuParts = baseSku.split('-');
    const baseSkuWithSize = selectedSize 
      ? `${baseSkuParts[0]}-${baseSkuParts[1]}-${selectedSize}`
      : baseSku;
    this.bundleProduct.skuInput.value = `${baseSkuWithSize}-${middleSku}`;
  }

  parseColorsToSelectElements() {
    const options = this.querySelectorAll('.material-select');
    options.forEach(option => {
      const optionValue = option.value;
      const selectedColor = MaterialsColorsManager.colors[optionValue];
      if (selectedColor) {
        option.innerText = selectedColor[this.shopLocale];
        option.dataset.order = selectedColor.order;
      }
    });
    this.sortOptionsByOrder();
  }

  sortOptionsByOrder() {
    const selects = this.querySelectorAll('select');
    selects.forEach(select => {
      const options = Array.from(select.options);
      if (options[0]?.disabled) { // Skip the first disabled option (placeholder)
        const firstOption = options.shift();
        options.sort((a, b) => {
          return parseInt(a.dataset.order || 0) - parseInt(b.dataset.order || 0);
        });
        options.unshift(firstOption);
        select.innerHTML = '';
        options.forEach(option => select.appendChild(option));
      } else {
        options.sort((a, b) => {
          return parseInt(a.dataset.order || 0) - parseInt(b.dataset.order || 0);
        });
        select.innerHTML = '';
        options.forEach(option => select.appendChild(option));
      }
    });
  }

  setupMetafieldSelectListeners() {
    const productTitle = this.getAttribute('data-bundle-metafield');
    const bundleAdetIndex = this.getAttribute('data-bundle-adet-index');
    const form = document.querySelector('product-form form');
    const skuInput = form.querySelector(`input[name="properties[_${productTitle}-${bundleAdetIndex}-Sku]"]`);
    const ringSizeInput = form.querySelector(`input[name="properties[_${productTitle}-${bundleAdetIndex}-RingSize]"]`);
    const initialSku = skuInput.getAttribute('data-initial-sku');
    
    this.bundleProduct = {
      initialSku: initialSku,
      skuInput: skuInput,
      ringSizeInput: ringSizeInput,
      selectedValues: []
    };

    const metafieldSelects = this.querySelectorAll('.materialsValues');
    metafieldSelects.forEach(select => {
      select.addEventListener('change', (event) => {
        this.handleMetafieldChange(event);
      });
    });
  }

  handleMetafieldChange(event) {
    const selectedOption = event.target;
    
    // Handle ring size input change
    if (selectedOption.type === 'text') {
      this.bundleProduct.ringSizeInput.value = selectedOption.value;
      return;
    }

    // Handle metafield select change
    const selectedOrderItem = MaterialsColorsManager.selectOrderMap[selectedOption.name];
    const selectedOrderItemSku = selectedOrderItem.sku.toUpperCase();

    selectedOption.nextElementSibling?.classList.contains('hidden')
      ? null
      : selectedOption.nextElementSibling?.classList.add('hidden');

    this.bundleProduct.selectedValues = this.bundleProduct.selectedValues.filter(item => item.sku !== selectedOrderItemSku);

    this.bundleProduct.selectedValues.push({
      value: selectedOption.value,
      label: selectedOption.id,
      order: selectedOrderItem.order,
      sku: selectedOrderItemSku,
    });

    this.updateSku();
  }
}

class RingSizeManager {
  constructor(container) {
    this.container = container;
    this.region = document.getElementById('region');
    this.selectedRegionForRs = this.region?.value;
    this.ringTable = document.getElementById('ring-data');
    const productTitle = this.container.getAttribute('data-bundle-metafield');
    const form = document.querySelector('product-form form');
    this.ringInput = form.querySelector(`input[name="properties[_${productTitle}-RingSize]"]`);
    this.ringInputShow = this.container.querySelector('input[type="text"].materialsValues');
    this.init();
  }

  init() {
    this.mapRingData();
    this.ringInputShow.addEventListener('input', (e) => {
      this.ringInput.value = e.target.value;
    });
    this.region.addEventListener('change', (e) => this.changeRegion(e));
  }

  changeRegion(e) {
    if (this.selectedRegionForRs === e.target.value) return;
    this.selectedRegionForRs = e.target.value;
    this.ringTable.innerHTML = '';
    this.mapRingData();
  }

  mapRingData() {
    ringSizes.map((size) => {
      const row = document.createElement('tr');
      row.classList = "rowClass";
      row.onclick = (e) => {
        e.stopPropagation();
        this.ringInput.value = `${size.circumference} mm`;
        this.ringInputShow.value = `${size.circumference} mm`;
        handleModal('close');
      };
      row.innerHTML = `
        <td class="tdMainClass">
          <span class="btnClass"> 
            ${size.circumference} mm
            </span>
        </td>
        <td class="tdClass">${size.diameter} mm</td>
        <td class="tdClass">${size[this.selectedRegionForRs]}</td>
      `;
      this.ringTable.appendChild(row);
    });
  }
}

function handleModal(type) {
  const modalContainer = document.querySelector('.modal-container');
  const modalElements = {
    ip: modalContainer.querySelector('[data-ip-renk]'),
    mine: modalContainer.querySelector('[data-mine-renk]'),
    rs: modalContainer.querySelector('.ring-drawer') // ringsize
  };

  const handleAnimation = (animation, callback) => {
    modalContainer.classList.add(animation);
    modalContainer.addEventListener('animationend', () => {
      modalContainer.classList.remove(animation);
      callback?.();
    }, { once: true });
  };

  if (type === 'close') {
    handleAnimation('fadeOutAnimation', () => modalContainer.classList.add('hidden'));
    return;
  }

  modalContainer.classList.remove('hidden');
  handleAnimation('fadeInAnimation');

  Object.values(modalElements).forEach(element => element.classList.add('hidden'));
  
  modalElements[type]?.classList.remove('hidden');
}

window.handleModal = handleModal;

// Setup buy button listeners
function setupBuyButtonListeners() {
  const fakeBuyButtons = document.querySelectorAll('[data-fakebuybutton]');

  fakeBuyButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const allMetafieldSelects = document.querySelectorAll('.materialsValues');
      
      if (allMetafieldSelects.length === 0) {
        clickRealBuyButton(btn, btn.nextElementSibling);
        return;
      }

      const unselectedSelects = Array.from(allMetafieldSelects).filter(select => select.value === "");
      if (unselectedSelects.length) {
        unselectedSelects.forEach(select => {
          const errorText = select.nextElementSibling;
          if (errorText) {
            errorText.classList.remove('hidden');
            errorText.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorText.classList.add('highlight-on-scroll');
            errorText.addEventListener('animationend', () => {
              errorText.classList.remove('highlight-on-scroll');
            }, { once: true });
          }
        });
      } else {
        clickRealBuyButton(btn, btn.nextElementSibling);
      }
    });
  });
}

function clickRealBuyButton(fakeButton, realButton) {
  fakeButton.classList.add('loading');
  realButton.click();
  setTimeout(() => {
    fakeButton.classList.remove('loading');
  }, 1250);
}

// Register custom element and initialize buy button listeners
customElements.define('bundle-product-manager', BundleProductManager);
setupBuyButtonListeners();
